import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import MonacoEditor, {
  BeforeMount,
  Monaco,
  OnMount,
} from '@monaco-editor/react';
import { editor as monacoEditor } from 'monaco-editor';
import { cn } from '../../lib/utils';
import { useColorMode } from '../../contexts/ColorModeContext';
import {
  databricksSqlDarkTheme,
  databricksSqlTheme,
} from '../databricksSqlEditor/databricksTheme';

export interface JsonEditorProps {
  value: object | null;
  height?: string;
  theme?: 'light' | 'dark';
  readOnly?: boolean;
  className?: string;
  onContextMenu?: (
    path: string | null,
    lineNumber: number,
    selectedText: string | null,
    event: MouseEvent,
  ) => void;
}

export interface JsonEditorRef {
  getValue: () => string;
  setValue: (value: string) => void;
  focus: () => void;
  getMonacoInstance: () => monacoEditor.IStandaloneCodeEditor | null;
}

export const JsonEditor = forwardRef<JsonEditorRef, JsonEditorProps>(
  (
    {
      value,
      height = '400px',
      theme,
      readOnly = true,
      className,
      onContextMenu,
    },
    ref,
  ) => {
    const editorRef = useRef<monacoEditor.IStandaloneCodeEditor | null>(null);
    const monacoRef = useRef<Monaco | null>(null);
    const lineToPathMapRef = useRef<Map<number, string>>(new Map());
    const { isDarkMode } = useColorMode();

    const isDark = theme ? theme === 'dark' : isDarkMode;
    const jsonString = value ? JSON.stringify(value, null, 2) : '';

    // Build line-to-path mapping for JSON navigation
    const buildLineToPathMap = useCallback(
      (jsonStr: string): Map<number, string> => {
        const lines = jsonStr.split('\n');
        const pathMap = new Map<number, string>();
        const pathStack: string[] = [];
        const contextStack: Array<'object' | 'array'> = [];
        const arrayIndexStack: number[] = [];

        lines.forEach((line, index) => {
          const lineNumber = index + 1;
          const trimmed = line.trim();

          if (!trimmed) return;

          // Handle closing braces/brackets
          if (trimmed === '}' || trimmed === '},') {
            if (contextStack[contextStack.length - 1] === 'object') {
              pathStack.pop();
              contextStack.pop();
            }
          } else if (trimmed === ']' || trimmed === '],') {
            if (contextStack[contextStack.length - 1] === 'array') {
              pathStack.pop();
              contextStack.pop();
              arrayIndexStack.pop();
            }
          }

          // Handle array elements (lines that start with { in array context)
          if (
            contextStack.length > 0 &&
            contextStack[contextStack.length - 1] === 'array' &&
            (trimmed.startsWith('{') ||
              trimmed.match(/^"[^"]*"/) ||
              trimmed.match(/^\d+/) ||
              trimmed === 'true' ||
              trimmed === 'false' ||
              trimmed === 'null')
          ) {
            const arrayIndex = arrayIndexStack[arrayIndexStack.length - 1];
            const parentPath = pathStack.slice(0, -1).join('.');
            const currentPath = parentPath
              ? `${parentPath}[${arrayIndex}]`
              : `[${arrayIndex}]`;
            pathMap.set(lineNumber, currentPath);

            // Increment index for next element
            if (trimmed.endsWith(',')) {
              arrayIndexStack[arrayIndexStack.length - 1]++;
            }

            // If this starts an object, we need to track it
            if (trimmed.startsWith('{')) {
              pathStack.push(`[${arrayIndex}]`);
              contextStack.push('object');
            }
          }

          // Extract key from lines like "key": value
          const keyMatch = trimmed.match(/^"([^"]+)":\s*/);
          if (keyMatch) {
            const key = keyMatch[1];
            const fullPath =
              pathStack.length > 0 ? `${pathStack.join('.')}.${key}` : key;
            pathMap.set(lineNumber, fullPath);

            // Check if this opens a new object or array
            const afterKey = trimmed.slice(keyMatch[0].length);
            if (afterKey.startsWith('{')) {
              pathStack.push(key);
              contextStack.push('object');
            } else if (afterKey.startsWith('[')) {
              pathStack.push(key);
              contextStack.push('array');
              arrayIndexStack.push(0);
            }
          }
        });

        return pathMap;
      },
      [],
    );

    useImperativeHandle(ref, () => ({
      getValue: () => editorRef.current?.getValue() || '',
      setValue: (val: string) => editorRef.current?.setValue(val),
      focus: () => editorRef.current?.focus(),
      getMonacoInstance: () => editorRef.current,
    }));

    // Update theme when isDark changes
    useEffect(() => {
      if (monacoRef.current && editorRef.current) {
        monacoRef.current.editor.setTheme(isDark ? 'vs-dark' : 'vs');
      }
    }, [isDark]);

    // Update path map when value changes
    useEffect(() => {
      lineToPathMapRef.current = buildLineToPathMap(jsonString);
    }, [jsonString, buildLineToPathMap]);

    const options = useMemo(
      () => ({
        readOnly,
        minimap: { enabled: false },
        fontSize: 14,
        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
        scrollBeyondLastLine: false,
        wordWrap: 'on' as const,
        automaticLayout: true,
        lineNumbers: 'on' as const,
        lineDecorationsWidth: 0,
        lineNumbersMinChars: 3,
        folding: true,
        quickSuggestions: false,
        parameterHints: { enabled: false },
        suggestOnTriggerCharacters: false,
        showFoldingControls: 'mouseover' as const,
      }),
      [readOnly],
    );

    const handleEditorDidMount: OnMount = useCallback(
      (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;

        // Disable Monaco's default keyboard shortcuts that we don't want
        const shortcuts = [
          { key: monaco.KeyCode.F1 }, // Help
          {
            key:
              monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyP,
          }, // Command Palette
          { key: monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyP }, // Quick Open
          { key: monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyG }, // Go to Line
          {
            key:
              monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyO,
          }, // Go to Symbol
        ];

        shortcuts.forEach((shortcut, index) => {
          editor.addAction({
            id: `disable-shortcut-${index}`,
            label: '',
            keybindings: [shortcut.key],
            run: () => undefined,
          });
        });

        // Completely disable Monaco's context menu
        editor.onContextMenu((e) => {
          e.event.preventDefault();
          e.event.stopPropagation();

          if (onContextMenu) {
            const position = e.target.position;
            if (!position) return;

            const model = editor.getModel();
            if (!model) return;

            const lineNumber = position.lineNumber;
            const fullPath = lineToPathMapRef.current.get(lineNumber) || null;

            // Get selected text if any
            const selection = editor.getSelection();
            let selectedText: string | null = null;
            if (selection && !selection.isEmpty()) {
              selectedText = model.getValueInRange(selection).trim() || null;
            }

            onContextMenu(
              fullPath,
              lineNumber,
              selectedText,
              e.event.browserEvent,
            );
          }
        });

        // Disable the context menu service
        const contextMenuService = (
          editor as unknown as {
            _contextMenuService?: { showContextMenu: () => void };
          }
        )._contextMenuService;
        if (contextMenuService) {
          contextMenuService.showContextMenu = () => {};
        }

        // Disable the context menu contribution
        const contextMenuContribution = editor.getContribution(
          'editor.contrib.contextmenu',
        );
        if (
          contextMenuContribution &&
          typeof (contextMenuContribution as { dispose?: () => void })
            .dispose === 'function'
        ) {
          (contextMenuContribution as { dispose: () => void }).dispose();
        }

        // Update path map on content change
        editor.onDidChangeModelContent(() => {
          const newContent = editor.getValue();
          lineToPathMapRef.current = buildLineToPathMap(newContent);
        });

        // Initial path map
        lineToPathMapRef.current = buildLineToPathMap(jsonString);
      },
      [jsonString, buildLineToPathMap, onContextMenu],
    );

    const handleBeforeMount: BeforeMount = useCallback((monaco) => {
      // Register the databricks themes if they haven't been registered yet
      try {
        monaco.editor.defineTheme('databricks-light', databricksSqlTheme);
        monaco.editor.defineTheme('databricks-dark', databricksSqlDarkTheme);
      } catch {
        // Themes might already be defined, that's ok
      }
    }, []);

    return (
      <div
        className={cn('relative overflow-hidden rounded-sm', className)}
        style={{ height: height === '100%' ? '100%' : height, width: '100%' }}
      >
        <MonacoEditor
          height="100%"
          width="100%"
          language="json"
          value={jsonString}
          beforeMount={handleBeforeMount}
          onMount={handleEditorDidMount}
          options={options}
          theme={isDark ? 'databricks-dark' : 'databricks-light'}
        />
      </div>
    );
  },
);

JsonEditor.displayName = 'JsonEditor';
